import { get } from 'lodash';

/**
 * Short tag engine for data consumers.
 *
 * Example:
 *
 * ```
 * // template, usually retrieved from Contentful
 * const tmpl = "Buy now for {{teamsUnitPrice.unitPrice}} {{teamsUnitPrice.currencyCode}} {{#promo.promoCode}}with {{promo.percent}}% off{{/promo.promoCode}}";
 *
 * // extract the necessary data sources from NativeDataContext
 * const requiredDataSourceNames = extractRequiredDataSourceNames(description);
 * const context: Record<string, NativeData> = {};
 * for (const name of requiredDataSourceNames) {
 *   context[name] = getNativeData(name);
 * }
 *
 * // render the template
 * const text = renderTemplate(tmpl, context);
 * ```
 *
 * @module shortTags
 */

const dataSourceRegex = /\{\{\s*([#/^]?)\s*(\w+)\.([\w.]+)\s*\}\}/g;

/**
 * Token type.
 *
 * type can be:
 * - text: plain text
 * - data: data source reference
 * - '#': if condition
 * - '^': if not condition
 * - '/': closing tag
 */
export type Token = {
  type: 'text' | '#' | '^' | '/' | 'data';
  value: string;
  fullMatch: string;
};

/**
 * AST node type.
 *
 * type can be:
 * - text: plain text node
 * - data: data source reference node
 * - condition: if/if not condition node
 * - root: root node
 *
 * If the node is a condition node, it will have `if` and `not` properties.
 * `if` is the data source expression where the condition should be evaulated.
 * `not` is a boolean value indicating the condition should be inverted.
 *
 * If the node is a root node or a condition node, it will have `children` property.
 *
 * If the node is a text node or a data source reference node, it will have `value` property.
 *
 * A valid AST must be an ASTNode object with type `root`.
 */
export type ASTNode = {
  type: 'root' | 'text' | 'condition' | 'data';
  value?: string;
  if?: string;
  not?: boolean;
  children?: ASTNode[];
};

/**
 * Extracts data source names from text.
 * Useful for determining which data sources are required to render the text.
 * @param text Text to extract data source names from
 * @return Array of data source names
 */
export function extractRequiredDataSourceNames(text: string): string[] {
  const requiredDataSourceNames = text.match(dataSourceRegex)?.map((tag) => tag.replace(dataSourceRegex, '$2')) || [];

  return Array.from(new Set(requiredDataSourceNames));
}

/**
 * tokenize a string into an array of tokens
 * @param text Text to tokenize
 * @return List of tokens
 */
export function tokenize(text: string): Token[] {
  const tokens = [];
  let match;

  // `pos` to keep track of the current position in the text
  let pos = 0;

  // loop through the text and find all the tags
  match = dataSourceRegex.exec(text);
  while (match !== null) {
    const [fullMatch, type, dataSource, field] = match;

    // if there is any text between the last tag and this one, add it as a token
    if (match.index > pos) {
      const s = text.slice(pos, match.index);
      tokens.push({ type: 'text' as const, value: s, fullMatch: s });
    }

    // record the current position
    pos = match.index + fullMatch.length;

    // add the tag as a token
    tokens.push({
      type: (type || 'data') as Token['type'],
      value: `${dataSource}.${field}`,
      fullMatch,
    });

    // find the next tag
    match = dataSourceRegex.exec(text);
  }

  // if there is any text after the last tag, add it as a token
  if (pos < text.length) {
    const s = text.slice(pos);
    tokens.push({ type: 'text' as const, value: s, fullMatch: s });
  }

  return tokens;
}

/**
 * Create AST based on tokens.
 * @param tokens List of tokens
 * @return an AST tree.
 */
export function createAST(tokens: Array<Token>): ASTNode {
  // create the root node
  // the top of the `stack` is the current node.
  const stack: Array<ASTNode> = [
    {
      type: 'root',
      children: [],
    },
  ];

  for (const token of tokens) {
    switch (token.type) {
      // if the token is a text token, create a text node
      case 'text':
        stack[stack.length - 1].children?.push({ type: 'text', value: token.value });
        break;

      // if the token is a condition token, create a condition node and enter next level
      case '#':
      case '^': {
        const node: ASTNode = {
          type: 'condition',
          if: token.value,
          not: token.type === '^',
          children: [],
        };
        stack[stack.length - 1].children?.push(node);

        // enter next level
        stack.push(node);
        break;
      }

      // if the token is a closing tag, exit current level
      case '/':
        // the closing tag should match the last condition node
        if (token.value === stack[stack.length - 1].if) {
          stack.pop();
          break;
        } else if (stack[stack.length - 1].type === 'condition') {
          // close tag does not match the last condition node
          throw new Error(`Unbalanced closing tag: expect {{/${stack[stack.length - 1].if}}}, got {{/${token.value}}}`);
        } else {
          // orphan closing tag
          throw new Error(`Unexpected closing tag: {{/${token.value}}}`);
        }

      // if the token is a data token, create a data node
      case 'data':
        stack[stack.length - 1].children?.push({ type: 'data', value: token.value });
        break;

      default:
        throw new Error(`Unexpected token type: ${token.type}`);
    }
  }

  if (stack.length > 1) {
    const missingTags = stack.slice(1).map((node) => `{{/${node.if}}}`);
    missingTags.reverse();
    throw new Error(`Missing closing tag(s): ${missingTags.join(' ')}`);
  }

  return stack[0];
}

/**
 * Renders a given template.
 * @param template Text template
 * @param context Context object
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function renderTemplate(template: string, context: Record<string, any>): string {
  const tokens = tokenize(template);
  const ast = createAST(tokens);

  /**
   * Renders a given AST node.
   * This is a recursive function that renders the node and all its children.
   * @param node AST node
   * @returns List of rendered strings
   */
  function renderNode(node: ASTNode): string[] {
    if (!node.children) {
      throw new Error('Unexpected root node, "children" is required');
    }

    return node.children?.reduce<string[]>((acc, n) => {
      switch (n.type) {
        // text node, just return the text
        case 'text':
          if (!n.value) {
            throw new Error('Unexpected empty text node');
          }
          return [...acc, n.value];

        // condition node, evaluate the condition and render the true branch
        case 'condition': {
          if (!n.if) {
            throw new Error(`Invalid condition node: ${JSON.stringify(n)}, "if" is required`);
          }

          const value = get(context, n.if);
          if (n.not) {
            return [...acc, ...(value ? [] : renderNode(n))];
          } else {
            return [...acc, ...(value ? renderNode(n) : [])];
          }
        }

        // data node, get the value from the context and return it
        case 'data': {
          if (!n.value) {
            throw new Error(`Invalid data node: ${JSON.stringify(n)}, "value" is required`);
          }

          const value = get(context, n.value);
          return [...acc, value];
        }

        default:
          throw new Error(`Unexpected node type: ${node.type}`);
      }
    }, []);
  }

  return renderNode(ast).join('');
}
